import { Icon, type IconName } from "@/components/icons/Icon";
import { classNames } from "@/utils/classnames";
import React from "react";

const COUNT_BADGE_BASE =
  "inline-flex h-cds-16 shrink-0 cursor-default items-center justify-center rounded-cds-3 " +
  "px-cds-4 !text-cds-2xs font-medium leading-4 tabular-nums";

const COUNT_BADGE_VARIANTS = {
  light: "bg-cds-surface-active text-cds-ink-secondary",
  dark: "dark:!bg-white/[0.08] dark:!text-zinc-400",
} as const;

const COUNT_BADGE_KBD_STROKE = "ring-1 ring-inset ring-zinc-300 dark:!ring-0";
// Key badges can render outside the typography wrapper in portals, so pin them to Inter.
const COUNT_BADGE_KBD_FONT_STYLE: React.CSSProperties = {
  fontFamily:
    '"Chalk Dashboard Inter", "Inter Variable", Inter, ui-sans-serif, system-ui, sans-serif',
};

const COUNT_BADGE_EMPHASIS = {
  default: "",
  unhovered:
    "bg-cds-surface-active text-cds-ink-subtle " +
    "group-hover:bg-cds-surface-hover group-hover:text-cds-ink-secondary " +
    "dark:!bg-white/[0.04] dark:!text-zinc-500 " +
    "dark:group-hover:!bg-white/[0.08] dark:group-hover:!text-zinc-400",
} as const;

export type CountBadgeV2Props = Omit<React.HTMLAttributes<HTMLElement>, "children"> & {
  as?: "span" | "kbd" | "button";
  children?: string;
  emphasis?: keyof typeof COUNT_BADGE_EMPHASIS;
  icon?: IconName;
  loading?: boolean;
  loadingLabel?: string;
  type?: React.ButtonHTMLAttributes<HTMLButtonElement>["type"];
  withStroke?: boolean;
};

const CountBadgeV2Base = React.forwardRef<HTMLElement, CountBadgeV2Props>(function CountBadgeV2Impl(
  {
    as = "span",
    "aria-busy": ariaBusy,
    "aria-label": ariaLabel,
    children,
    className,
    emphasis = "default",
    icon,
    loading = false,
    loadingLabel = "Loading count",
    style,
    type,
    withStroke: _withStroke = false,
    ...rest
  },
  ref,
) {
  return React.createElement(
    as,
    {
      ...rest,
      ref,
      "data-count-badge-v2": "",
      "aria-busy": loading ? true : ariaBusy,
      "aria-label": loading ? ariaLabel ?? loadingLabel : ariaLabel,
      ...(as === "button" ? { type: type ?? "button" } : {}),
      style: as === "kbd" ? { ...COUNT_BADGE_KBD_FONT_STYLE, ...style } : style,
      className: classNames(
        COUNT_BADGE_BASE,
        COUNT_BADGE_VARIANTS.light,
        COUNT_BADGE_VARIANTS.dark,
        COUNT_BADGE_EMPHASIS[emphasis],
        icon != null && !loading && "gap-cds-4",
        as === "kbd" && COUNT_BADGE_KBD_STROKE,
        className,
      ),
    },
    loading ? (
      <span
        aria-hidden={true}
        className={"h-cds-4 w-cds-8 rounded-full bg-current opacity-60 animate-placeholder-loading"}
      />
    ) : (
      <>
        {icon != null && <Icon aria-hidden={true} name={icon} size={"xs"} />}
        {icon != null ? <span>{children}</span> : children}
      </>
    ),
  );
});

export const CountBadgeV2 = React.memo(CountBadgeV2Base);
CountBadgeV2.displayName = "CountBadgeV2";
